import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { getSectionWiseDailySummary } from "../../api/attendanceReportApi";
import "./Attendance.css";

const periodApi = createLookupApi("periods");
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Direct port of digicampusbd's `student-attendance-report-shift-wise-daily-summary`
 * ("Date Wise Shift Summary") — confirmed via `shiftWiseDailyAttendanceSummary`
 * that this reuses the EXACT same native SQL as `-section-wise-daily-summary`
 * (only filtered to active sections server-side); the "shift" grouping isn't
 * done server-side at all in the old app — the frontend groups the flat
 * section-list client-side. Reuses the same `GET
 * /api/attendance/reports/student/section-wise-daily-summary` endpoint with
 * `activeOnly=true`, then groups by `shiftName` here.
 */
export default function ShiftWiseDailySummaryPage() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    periodApi.list().then((list) => {
      setPeriods(list);
      if (list.length > 0) setPeriodId(String(list[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setRows(await getSectionWiseDailySummary(date, periodId, true));
    } catch (err) {
      setError("Failed to load shift summary.");
    } finally {
      setLoading(false);
    }
  };

  const shiftGroups = useMemo(() => {
    const groups = new Map();
    (rows || []).forEach((r) => {
      if (!groups.has(r.shiftName)) groups.set(r.shiftName, []);
      groups.get(r.shiftName).push(r);
    });
    return Array.from(groups.entries());
  }, [rows]);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Date Wise Shift Summary</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Period
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows &&
        (shiftGroups.length === 0 ? (
          <p>No section took attendance for this selection.</p>
        ) : (
          shiftGroups.map(([shiftName, sections]) => {
            const taken = sections.reduce((s, r) => s + r.attendanceTaken, 0);
            const present = sections.reduce((s, r) => s + r.present, 0);
            const absent = sections.reduce((s, r) => s + r.absent, 0);
            const leave = sections.reduce((s, r) => s + r.leave, 0);
            return (
              <div key={shiftName} className="table-scroll" style={{ marginBottom: 20 }}>
                <h3>{shiftName}</h3>
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Taken</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Leave</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.map((r) => (
                      <tr key={r.classConfigId}>
                        <td>{r.className}</td>
                        <td>{r.sectionName}</td>
                        <td>{r.attendanceTaken}</td>
                        <td>{r.present}</td>
                        <td>{r.absent}</td>
                        <td>{r.leave}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ fontWeight: 800, textAlign: "right" }}>
                        Shift Total
                      </td>
                      <td>
                        <strong>{taken}</strong>
                      </td>
                      <td>
                        <strong>{present}</strong>
                      </td>
                      <td>
                        <strong>{absent}</strong>
                      </td>
                      <td>
                        <strong>{leave}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })
        ))
      )}
    </MainLayout>
  );
}
